<template>
  <article class="project-card" @click="onCardClick">
    <div class="project-card__media">
      <div
        v-if="images.length"
        ref="track"
        class="project-card__slider"
        @scroll.passive="onScroll"
      >
        <!-- slides are links (tap navigates, swipe scrolls), hidden from
             assistive tech as duplicates of the title link -->
        <a
          v-for="(image, index) in images"
          :key="index"
          class="project-card__slide"
          :href="project.url"
          target="_blank"
          rel="noopener"
          tabindex="-1"
          aria-hidden="true"
        >
          <img class="project-card__image" :src="image" alt="" loading="lazy" draggable="false" />
        </a>
      </div>
      <div v-else class="project-card__image project-card__image--empty">
        <SvgIcon name="home" :size="32" />
        <span class="project-card__no-image">{{ t('listing-card.no-image') }}</span>
      </div>

      <span v-if="project.status" class="project-card__status" :data-status="project.status">
        <span class="project-card__status-dot" />
        {{ project.status }}
      </span>

      <template v-if="images.length > 1">
        <button
          v-if="current > 0"
          type="button"
          class="project-card__nav project-card__nav--prev"
          :aria-label="t('listing-card.image-prev')"
          @click="goTo(current - 1)"
        >
          <SvgIcon name="chevron" :size="14" :stroke="2" />
        </button>
        <button
          v-if="current < images.length - 1"
          type="button"
          class="project-card__nav project-card__nav--next"
          :aria-label="t('listing-card.image-next')"
          @click="goTo(current + 1)"
        >
          <SvgIcon name="chevron" :size="14" :stroke="2" />
        </button>
        <div class="project-card__dots">
          <button
            v-for="(image, index) in images"
            :key="index"
            type="button"
            class="project-card__dot"
            :class="{ 'is-active': index === current }"
            :aria-label="t('listing-card.image-goto', { index: index + 1 })"
            @click="goTo(index)"
          />
        </div>
      </template>
    </div>

    <div class="project-card__body">
      <!-- Phase inline after the project name (WHN-2130): they read as one
           heading and the phase flows onto the next line when the two together
           are too long. Same pattern as ListingCard's location + phase. -->
      <h3 class="project-card__title">
        <a :href="project.url" target="_blank" rel="noopener" class="project-card__link">
          {{ project.project }}
        </a><span v-if="project.subProject" class="project-card__phase"> – {{ project.subProject }}</span>
      </h3>

      <p v-if="locationLabel" class="project-card__location">
        <SvgIcon name="pin" :size="16" />
        <span>{{ locationLabel }}</span>
      </p>

      <ul v-if="specs.length" class="project-card__specs">
        <li v-for="spec in specs" :key="spec.icon" class="project-card__spec" :aria-label="spec.aria">
          <SvgIcon :name="spec.icon" :size="16" />
          <span aria-hidden="true">{{ spec.value }}</span>
        </li>
      </ul>
    </div>
  </article>
</template>

<script setup>
import { computed, ref } from 'vue';
import SvgIcon from './SvgIcon.vue';
import { useProjectDetail } from '../lib/useProjectDetail';
import { projectSpecs } from '../lib/projectSpecs';
import { t } from '../lib/i18n';

const props = defineProps({
  project: { type: Object, required: true }
});

const { open } = useProjectDetail();

// The whole card opens the detail panel, the same interaction ListingCard has.
// The title and slide links keep their href, so cmd/ctrl-click and crawlers
// still reach the real project page; only a plain click is intercepted.
// Buttons are the carousel controls and keep their own behaviour.
const onCardClick = (event) => {
  if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
  if (event.target.closest('button')) return;
  event.preventDefault();
  open(props.project);
};

const images = computed(() =>
  (props.project.images || []).map((image) => image.url || image.src || image).filter(Boolean)
);

const track = ref(null);
const current = ref(0);

const onScroll = () => {
  const el = track.value;
  if (el) current.value = Math.round(el.scrollLeft / el.clientWidth);
};

const goTo = (index) => {
  const el = track.value;
  if (el) el.scrollTo({ left: index * el.clientWidth, behavior: 'smooth' });
};

const locationLabel = computed(() =>
  [props.project.city, props.project.region].filter(Boolean).join(', ')
);

const specs = computed(() => projectSpecs(props.project));
</script>
